import { Crown, Sparkles, Calculator, Share2, Users, Gift, Star } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const VARIANTS = {
  subscribe: {
    ar: {
      badge: "قبل أن تغادر",
      title: "فرصة واحدة قبل ما تغلق الصفحة ",
      subtitle:
        "استعمل حاسبة برولانسر بدون حدود وافتح اقتراحات أسعار لامحدودة (مبنية على السوق السعودي + AI) بـ ٩٩ ر.س/شهر فقط — بدون تجديد تلقائي.",
      bullets: [
        "اقتراحات أسعار لامحدودة شهريًا — تحديثات يومية للسوق السعودي",
        "تسعير دقيق على أسعار السوق السعودي ٢٠٢٦",
        "دفعة واحدة عبر Tap · إلغاء بأي وقت",
      ],
      primary: "اشترك شهري ٩٩ ر.س",
      secondary: "تابع التصفح",
      icon: "crown",
    },
    en: {
      badge: "Before you leave",
      title: "One chance before you close this page ",
      subtitle: "Use the Prolancer Calculator without limits and unlock unlimited price suggestions (Saudi market + AI) for just 99 SAR/month — no auto-renewal.",
      bullets: [
        "Unlimited price suggestions · daily Saudi market updates",
        "Accurate pricing on 2026 Saudi market rates",
        "One-time Tap payment · cancel anytime",
      ],
      primary: "Subscribe · 99 SAR / mo",
      secondary: "Keep browsing",
      icon: "crown",
    },
  },
  yearly: {
    ar: {
      badge: "عرض خاص للمشتركين",
      title: "رقّي اشتراكك سنوي ووفّر ١٧٪ ",
      subtitle:
        "استمر باقتراحات أسعار لامحدودة (السوق السعودي + AI) بسعر أقل، مع التزام سنة كاملة — بدون تجديد تلقائي.",
      bullets: [
        "٤٩٩ ر.س بدل ١١٨٨ ر.س (توفير ٦٨٩ ر.س)",
        "لامحدود طوال السنة · فير-يوز ٥٠/يوم",
        "دفعة واحدة عبر Tap · آمن",
      ],
      primary: "ترقية سنوي ٤٩٩ ر.س",
      secondary: "تابع التصفح",
      icon: "crown",
    },
    en: {
      badge: "Subscriber-only offer",
      title: "Upgrade to yearly · save 17% ",
      subtitle:
        "Keep unlimited price suggestions (Saudi market + AI) at a lower annual price with a full-year commitment — no auto-renewal.",
      bullets: [
        "499 SAR instead of 1188 SAR (save 689 SAR)",
        "Unlimited all year · fair-use 50/day",
        "One-time Tap payment · secure",
      ],
      primary: "Upgrade · 499 SAR / yr",
      secondary: "Keep browsing",
      icon: "crown",
    },
  },
  share: {
    ar: {
      badge: "قبل أن تغادر",
      title: "ساعد زملاءك المستقلين — شارك الحاسبة ",
      subtitle:
        "اشتراكك فعّال ✓. انشر الحاسبة مع مستقل سعودي يُقدّم خدمات بأقل من سعرها الحقيقي — خطوة واحدة تساعده يُضاعف دخله.",
      bullets: [
        "واتساب · تويتر · لنكدإن بضغطة واحدة",
        "رابط مرجعي للنتائج يستفيد منه كل مستقل",
        "دعم مجتمع المستقلين السعوديين",
      ],
      primary: "شارك الحاسبة الآن",
      secondary: "لاحقًا",
      icon: "share",
    },
    en: {
      badge: "Before you leave",
      title: "Help fellow freelancers · share the calculator ",
      subtitle:
        "Your subscription is active ✓. Share Prolancer with a Saudi freelancer who's underpricing — one tap could 10x their income.",
      bullets: [
        "WhatsApp · Twitter · LinkedIn · one tap",
        "Instant pricing link anyone can use",
        "Support the Saudi freelance community",
      ],
      primary: "Share now",
      secondary: "Later",
      icon: "share",
    },
  },
};

export default function ExitIntentModal({
  open,
  onOpenChange,
  lang = "ar",
  theme = "dark",
  variant = "subscribe",
  onPrimary,
}) {
  const dark = theme === "dark";
  const v = VARIANTS[variant] || VARIANTS.subscribe;
  const L = v[lang === "ar" ? "ar" : "en"];
  const Icon = L.icon === "share" ? Share2 : Crown;
  const font = lang === "ar" ? "font-arabic" : "font-outfit";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="exit-intent-modal"
        data-variant={variant}
        dir={lang === "ar" ? "rtl" : "ltr"}
        className={`${font} max-w-md sm:max-w-lg p-0 overflow-hidden border-0 ${
          dark ? "bg-[#182138] text-white" : "bg-white text-[#182138]"
        }`}
      >
        <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-[#18c98f]/35 via-[#18c98f]/15 to-transparent">
          <div className="absolute top-2.5 left-2.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-[#18c98f] text-white text-[11px] font-extrabold px-2.5 py-1">
              <Sparkles className="h-3 w-3" />
              {L.badge}
            </span>
          </div>
          <DialogHeader className={`${lang === "ar" ? "text-right" : "text-left"} space-y-2 mt-6`}>
            <DialogTitle
              data-testid="exit-modal-title"
              className={`text-xl sm:text-2xl font-extrabold leading-snug ${
                dark ? "text-white" : "text-[#182138]"
              }`}
            >
              {L.title}
            </DialogTitle>
            <DialogDescription
              className={`text-sm leading-relaxed ${dark ? "text-white/70" : "text-[#2d2c35]/80"}`}
            >
              {L.subtitle}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 pb-6 pt-2 space-y-4">
          <ul className={`space-y-2 text-sm ${dark ? "text-white/85" : "text-[#2d2c35]"}`}>
            {L.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#18c98f] shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <button
            data-testid="exit-modal-primary"
            onClick={() => {
              onOpenChange(false);
              if (typeof onPrimary === "function") onPrimary(variant);
            }}
            className="w-full h-12 rounded-full font-extrabold text-sm inline-flex items-center justify-center gap-2 bg-[#18c98f] text-white hover:brightness-110 shadow-[0_8px_20px_-8px_rgba(24,201,143,0.7)] transition-all"
          >
            <Icon className="h-4 w-4" />
            {L.primary}
          </button>

          <button
            data-testid="exit-modal-dismiss"
            onClick={() => onOpenChange(false)}
            className={`w-full h-11 rounded-full text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors ${
              dark
                ? "bg-[#1f2a47] text-white/80 hover:text-white border border-[#2e3a5c] hover:border-[#18c98f]/50"
                : "bg-[#f3f8ff] text-[#2d2c35]/85 hover:text-[#182138] border border-[#e5e5ea] hover:border-[#18c98f]/50"
            }`}
          >
            <Calculator className="h-4 w-4" />
            {L.secondary}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
